import type { PrimitiveKind } from "@/document/model/primitives";
import type { BooleanOperation } from "@/document/model/nodes";
import type { EditorMode } from "@/editor/shell/EditorShell";
import type { ActiveTool } from "@/editor/shell/TopBar";
import type { AlignmentAction } from "@/editor/shell/right-panel/alignment";
import type { RenderPreview } from "@/renderer/overlayTypes";
import type { ShortcutAction } from "./keymap";

export type EditorAction = ShortcutAction;

interface EditorActionContext {
  mode: EditorMode;
  devModeActive: boolean;
  animationPanelOpen: boolean;
  vectorTargetNodeId: string | undefined;
  hasVectorTargetingSubselection: boolean;
  hasSelection: boolean;
  hasGuideSelection: boolean;
  hasVectorSelection: boolean;
  hasVectorVertexSelection: boolean;
  isColorSamplerActive: boolean;
  selectedCount: number;
  setPreviews: (previews: RenderPreview[]) => void;
  setTool: (tool: ActiveTool) => void;
  setShapeTool: (kind: PrimitiveKind) => void;
  clearSelection: () => void;
  selectAll: () => void;
  startSelectionColorSample: () => void;
  openSelectionPaint: (kind: "fill" | "stroke") => void;
  cancelColorSample: () => void;
  cancelPathDrawing: () => boolean;
  cancelSubdivide?: () => boolean;
  commitSubdivideSession?: () => boolean;
  exitVectorMode: () => void;
  exitTextMode: () => void;
  enterEditMode: () => void;
  isVectorTargeting: () => boolean;
  exitVectorTargeting: () => void;
  clearVectorTargetingSelection: () => void;
  deleteSelection: (options?: { reconnectVertices?: boolean }) => void;
  copySelection: () => void;
  cutSelection: () => void;
  pasteSelection: () => void;
  copyStyle: () => void;
  pasteStyle: () => void;
  duplicateSelection: () => void;
  createComponentSelection: () => void;
  swapFillAndStroke: () => void;
  groupSelection: () => void;
  frameSelection: () => void;
  ungroupSelection: () => void;
  booleanSelection: (operation: BooleanOperation) => void;
  outlineStrokeSelection: () => void;
  flattenSelection: () => void;
  flattenPlanarizedSelection: () => void;
  reorderSelection: (placement: "front" | "back" | "forward" | "backward") => void;
  alignSelection: (action: AlignmentAction) => void;
  flipSelection: (axis: "horizontal" | "vertical") => void;
  nudgeSelection: (dx: number, dy: number) => void;
  undo: () => void;
  redo: () => void;
  resetZoom: () => void;
  toggleUiChrome: () => void;
  toggleFloatingToolChrome: () => void;
  toggleFloatingToolSidebarChrome: () => void;
  toggleAnimationPreviewWindow: () => void;
  toggleAllowScripts: () => void;
  toggleScriptsDebugWindow: () => void;
  toggleRulers: () => void;
  toggleRenderDebug: () => void;
  adjustSculptBrush?: (property: "size" | "strength", delta: number) => void;
  setMessage: (message: string) => void;
  openDocument: () => void;
  newDocument: () => void;
  saveDocument: () => void;
  saveDocumentAs: () => void;
}

export function createEditorActions(context: EditorActionContext): EditorAction[] {
  return [
    {
      id: "file.new",
      label: "New",
      shortcuts: [{ key: "n", mod: true }],
      run: () => context.newDocument(),
    },
    {
      id: "file.open",
      label: "Open",
      shortcuts: [{ key: "o", mod: true }],
      run: () => context.openDocument(),
    },
    {
      id: "file.save",
      label: "Save",
      shortcuts: [{ key: "s", mod: true }],
      run: () => context.saveDocument(),
    },
    {
      id: "file.saveAs",
      label: "Save As",
      shortcuts: [{ key: "s", mod: true, shift: true }],
      run: () => context.saveDocumentAs(),
    },
    {
      id: "view.toggleUiChrome",
      label: "Toggle UI Chrome",
      shortcuts: [{ key: "\\", mod: true }],
      run: () => context.toggleUiChrome(),
    },
    {
      id: "view.toggleFloatingToolChrome",
      label: "Toggle Floating Tool Chrome",
      shortcuts: [{ key: "\\", mod: true, shift: true }],
      run: () => context.toggleFloatingToolChrome(),
    },
    {
      id: "view.toggleFloatingToolSidebarChrome",
      label: "Toggle Floating Tool Chrome With Sidebar",
      shortcuts: [{ key: "\\", mod: true, alt: true }],
      run: () => context.toggleFloatingToolSidebarChrome(),
    },
    {
      id: "view.toggleAnimationPreviewWindow",
      label: "Show Animation Preview Window",
      shortcuts: [{ key: "p", mod: true, shift: true }],
      run: () => context.toggleAnimationPreviewWindow(),
    },
    {
      id: "view.toggleAllowScripts",
      label: "Allow Scripts",
      shortcuts: [],
      run: () => context.toggleAllowScripts(),
    },
    {
      id: "view.toggleScriptsDebugWindow",
      label: "Show Scripts Debug",
      shortcuts: [],
      run: () => context.toggleScriptsDebugWindow(),
    },
    {
      id: "view.toggleRulers",
      label: "Toggle Rulers",
      shortcuts: [{ key: "r", shift: true }],
      run: () => context.toggleRulers(),
    },
    {
      id: "view.toggleRenderDebug",
      label: "Toggle Render Debug",
      shortcuts: [{ key: "~", shift: "optional" }],
      run: () => context.toggleRenderDebug(),
    },
    {
      id: "view.resetZoom",
      label: "Zoom to 100%",
      shortcuts: [{ key: "0", mod: true }],
      run: () => context.resetZoom(),
    },
    {
      id: "history.undo",
      label: "Undo",
      shortcuts: [{ key: "z", mod: true }],
      run: () => context.undo(),
    },
    {
      id: "history.redo",
      label: "Redo",
      shortcuts: [{ key: "z", mod: true, shift: true }],
      run: () => context.redo(),
    },
    {
      id: "tool.select",
      label: "Move Tool",
      shortcuts: [{ key: "v" }],
      run: () => {
        context.setTool("select");
        context.setMessage("Move tool");
      },
    },
    {
      id: "tool.frame",
      label: "Frame Tool",
      shortcuts: [{ key: "f" }, { key: "a" }],
      isEnabled: () => !context.devModeActive,
      run: () => {
        context.setTool("frame");
        context.setMessage("Frame tool");
      },
    },
    {
      id: "tool.path",
      label: "Path Tool",
      shortcuts: [{ key: "p" }],
      isEnabled: () => !context.devModeActive,
      run: () => {
        context.setTool("path");
        context.setMessage("Path tool");
      },
    },
    {
      id: "tool.pencil",
      label: "Pencil Tool",
      shortcuts: [{ key: "p", shift: true }],
      isEnabled: () => !context.devModeActive,
      run: () => {
        context.setTool("pencil");
        context.setMessage("Pencil tool");
      },
    },
    {
      id: "tool.lasso",
      label: "Lasso Tool",
      shortcuts: [{ key: "q" }],
      isEnabled: () => !context.devModeActive,
      run: () => {
        context.setTool("lasso");
        context.setMessage("Lasso tool");
      },
    },
    {
      id: "tool.fill",
      label: "Fill Tool",
      shortcuts: [{ key: "g" }, { key: "b", shift: true }],
      isEnabled: () => !context.devModeActive && context.mode === "vectorEdit",
      run: () => {
        context.setTool("fill");
        context.setMessage("Fill tool");
      },
    },
    {
      id: "paint.openFill",
      label: "Edit Selection Fill",
      shortcuts: [{ key: "g" }],
      isEnabled: () => !context.devModeActive && context.mode === "select" && context.hasSelection,
      run: () => context.openSelectionPaint("fill"),
    },
    {
      id: "paint.openStroke",
      label: "Edit Selection Stroke",
      shortcuts: [{ key: "g", shift: true }],
      isEnabled: () => !context.devModeActive && context.mode === "select" && context.hasSelection,
      run: () => context.openSelectionPaint("stroke"),
    },
    {
      id: "tool.sculpt",
      label: "Sculpt Brush",
      shortcuts: [{ key: "b" }],
      isEnabled: () => !context.devModeActive && context.mode === "vectorEdit",
      run: () => {
        context.setTool("sculpt");
        context.setMessage("Sculpt brush");
      },
    },
    {
      id: "tool.extrude",
      label: "Extrude Tool",
      shortcuts: [{ key: "e" }],
      isEnabled: () => !context.devModeActive && context.mode === "vectorEdit",
      run: () => {
        context.setTool("extrude");
        context.setMessage("Extrude edges");
      },
    },
    {
      id: "sculpt.decreaseSize",
      label: "Decrease Sculpt Brush Size",
      shortcuts: [{ key: "[" }],
      isEnabled: () => !context.devModeActive && context.mode === "vectorEdit",
      run: () => context.adjustSculptBrush?.("size", -10),
    },
    {
      id: "sculpt.increaseSize",
      label: "Increase Sculpt Brush Size",
      shortcuts: [{ key: "]" }],
      isEnabled: () => !context.devModeActive && context.mode === "vectorEdit",
      run: () => context.adjustSculptBrush?.("size", 10),
    },
    {
      id: "sculpt.decreaseStrength",
      label: "Decrease Sculpt Brush Strength",
      shortcuts: [{ key: "{", shift: true }],
      isEnabled: () => !context.devModeActive && context.mode === "vectorEdit",
      run: () => context.adjustSculptBrush?.("strength", -5),
    },
    {
      id: "sculpt.increaseStrength",
      label: "Increase Sculpt Brush Strength",
      shortcuts: [{ key: "}", shift: true }],
      isEnabled: () => !context.devModeActive && context.mode === "vectorEdit",
      run: () => context.adjustSculptBrush?.("strength", 5),
    },
    {
      id: "tool.knife",
      label: "Knife Tool",
      shortcuts: [{ key: "k" }],
      isEnabled: () => !context.devModeActive && context.mode === "vectorEdit",
      run: () => {
        context.setTool("knife");
        context.setMessage("Knife tool");
      },
    },
    {
      id: "tool.shapeBuilder",
      label: "Shape Builder Tool",
      shortcuts: [{ key: "m" }],
      isEnabled: () => !context.devModeActive && context.mode === "vectorEdit",
      run: () => {
        context.setTool("shapeBuilder");
        context.setMessage("Shape builder");
      },
    },
    {
      id: "tool.text",
      label: "Text Tool",
      shortcuts: [{ key: "t" }],
      isEnabled: () => !context.devModeActive,
      run: () => {
        context.setTool("text");
        context.setMessage("Text tool");
      },
    },
    shapeToolAction("tool.rectangle", "Rectangle Tool", "r", "rectangle", context),
    shapeToolAction("tool.oval", "Oval Tool", "o", "oval", context),
    shapeToolAction("tool.line", "Line Tool", "l", "line", context),
    {
      id: "mode.cancel",
      label: "Cancel",
      shortcuts: [{ key: "escape", shift: "optional" }],
      run: () => {
        context.setPreviews([]);
        if (context.isColorSamplerActive) {
          context.cancelColorSample();
          context.setTool("select");
          context.setMessage("Color sampler off");
          return;
        }
        if (context.cancelSubdivide?.()) {
          return;
        }
        if (context.isVectorTargeting()) {
          if (context.hasVectorTargetingSubselection) {
            context.clearVectorTargetingSelection();
            return;
          }
          context.exitVectorTargeting();
          return;
        }
        if (context.cancelPathDrawing()) {
          return;
        }
        if (context.mode === "vectorEdit") {
          context.exitVectorMode();
          return;
        }
        if (context.mode === "textEdit") {
          context.exitTextMode();
          return;
        }

        context.clearSelection();
        context.setMessage("Selection cleared");
      },
    },
    {
      id: "mode.enterVectorEdit",
      label: "Enter Edit Mode",
      shortcuts: [{ key: "enter", shift: "optional" }],
      isEnabled: () => !context.devModeActive || context.animationPanelOpen,
      run: () => {
        if (context.commitSubdivideSession?.()) {
          return;
        }
        context.enterEditMode();
      },
    },
    {
      id: "edit.deleteSelectionReconnect",
      label: "Delete Vertex and Reconnect",
      shortcuts: [{ key: "delete", shift: true }, { key: "backspace", shift: true }],
      isEnabled: () => context.mode === "vectorEdit" && context.hasVectorVertexSelection,
      run: () => context.deleteSelection({ reconnectVertices: true }),
    },
    {
      id: "edit.deleteSelection",
      label: "Delete Selection",
      shortcuts: [{ key: "delete" }, { key: "backspace" }],
      isEnabled: () => (context.hasSelection || context.hasGuideSelection) && context.mode !== "textEdit",
      run: () => context.deleteSelection(),
    },
    {
      id: "tool.colorSample",
      label: "Sample Color",
      shortcuts: [{ key: "i" }],
      isEnabled: () => context.hasSelection && context.mode !== "textEdit",
      run: () => context.startSelectionColorSample(),
    },
    {
      id: "edit.selectAll",
      label: "Select All",
      shortcuts: [{ key: "a", mod: true }],
      isEnabled: () => context.mode !== "textEdit",
      run: () => context.selectAll(),
    },
    {
      id: "edit.copySelection",
      label: "Copy Selection",
      shortcuts: [{ key: "c", mod: true }],
      isEnabled: () => (context.hasSelection || context.hasVectorSelection) && context.mode !== "textEdit",
      run: () => context.copySelection(),
    },
    {
      id: "edit.cutSelection",
      label: "Cut Selection",
      shortcuts: [{ key: "x", mod: true }],
      isEnabled: () => (context.hasSelection || context.hasVectorSelection) && context.mode !== "textEdit",
      run: () => context.cutSelection(),
    },
    {
      id: "edit.pasteSelection",
      label: "Paste",
      shortcuts: [{ key: "v", mod: true }],
      isEnabled: () => context.mode !== "textEdit",
      run: () => context.pasteSelection(),
    },
    {
      id: "edit.copyStyle",
      label: "Copy Style",
      shortcuts: [{ key: "c", mod: true, alt: true }],
      isEnabled: () => context.hasSelection && context.mode !== "textEdit",
      run: () => context.copyStyle(),
    },
    {
      id: "edit.pasteStyle",
      label: "Paste Style",
      shortcuts: [{ key: "v", mod: true, alt: true }],
      isEnabled: () => context.hasSelection && context.mode !== "textEdit",
      run: () => context.pasteStyle(),
    },
    {
      id: "edit.duplicateSelection",
      label: "Duplicate Selection",
      shortcuts: [{ key: "d", mod: true }],
      isEnabled: () => context.hasSelection && context.mode !== "textEdit",
      run: () => context.duplicateSelection(),
    },
    {
      id: "object.createComponent",
      label: "Create Component",
      shortcuts: [{ key: "k", mod: true, alt: true }],
      isEnabled: () => context.hasSelection && context.mode !== "textEdit",
      run: () => context.createComponentSelection(),
    },
    {
      id: "object.swapFillAndStroke",
      label: "Swap Fill and Stroke",
      shortcuts: [{ key: "x", shift: true }],
      isEnabled: () => context.hasSelection && context.mode !== "textEdit",
      run: () => context.swapFillAndStroke(),
    },
    {
      id: "object.group",
      label: "Group Selection",
      shortcuts: [{ key: "g", mod: true }],
      isEnabled: () => context.selectedCount >= 2 && context.mode !== "textEdit",
      run: () => context.groupSelection(),
    },
    {
      id: "object.frameSelection",
      label: "Frame Selection",
      shortcuts: [{ key: "g", mod: true, alt: true }],
      isEnabled: () => context.hasSelection && context.mode !== "textEdit",
      run: () => context.frameSelection(),
    },
    {
      id: "object.ungroup",
      label: "Ungroup Selection",
      shortcuts: [{ key: "g", mod: true, shift: true }],
      isEnabled: () => context.hasSelection && context.mode !== "textEdit",
      run: () => context.ungroupSelection(),
    },
    booleanAction("object.booleanUnion", "Union Selection", "u", "union", context),
    booleanAction("object.booleanSubtract", "Subtract Selection", "s", "subtract", context),
    booleanAction("object.booleanIntersect", "Intersect Selection", "i", "intersect", context),
    booleanAction("object.booleanExclude", "Exclude Selection", "x", "exclude", context),
    {
      id: "object.outlineStroke",
      label: "Outline Stroke",
      shortcuts: [{ key: "o", mod: true, alt: true }],
      isEnabled: () => context.hasSelection && context.mode !== "textEdit",
      run: () => context.outlineStrokeSelection(),
    },
    {
      id: "object.flattenPlanarized",
      label: "Flatten and Planarize Selection",
      shortcuts: [{ key: "e", mod: true, shift: true }],
      isEnabled: () => context.hasSelection && context.mode !== "textEdit",
      run: () => context.flattenPlanarizedSelection(),
    },
    {
      id: "object.flatten",
      label: "Flatten Selection",
      shortcuts: [{ key: "e", mod: true }],
      isEnabled: () => context.hasSelection && context.mode !== "textEdit",
      run: () => context.flattenSelection(),
    },
    {
      id: "object.sendToBack",
      label: "Send to Back",
      shortcuts: [{ key: "[" }],
      isEnabled: () => context.hasSelection && context.mode !== "textEdit",
      run: () => context.reorderSelection("back"),
    },
    {
      id: "object.bringToFront",
      label: "Bring to Front",
      shortcuts: [{ key: "]" }],
      isEnabled: () => context.hasSelection && context.mode !== "textEdit",
      run: () => context.reorderSelection("front"),
    },
    {
      id: "object.sendBackward",
      label: "Send Backward",
      shortcuts: [{ key: "[", mod: true }],
      isEnabled: () => context.hasSelection && context.mode !== "textEdit",
      run: () => context.reorderSelection("backward"),
    },
    {
      id: "object.bringForward",
      label: "Bring Forward",
      shortcuts: [{ key: "]", mod: true }],
      isEnabled: () => context.hasSelection && context.mode !== "textEdit",
      run: () => context.reorderSelection("forward"),
    },
    alignAction("object.alignLeft", "Align left", "a", "left", context),
    alignAction("object.alignHorizontalCenter", "Align horizontal centers", "h", "horizontalCenter", context),
    alignAction("object.alignRight", "Align right", "d", "right", context),
    alignAction("object.alignTop", "Align top", "w", "top", context),
    alignAction("object.alignVerticalCenter", "Align vertical centers", "v", "verticalCenter", context),
    alignAction("object.alignBottom", "Align bottom", "s", "bottom", context),
    {
      id: "object.flipHorizontal",
      label: "Flip Horizontal",
      shortcuts: [{ key: "h", shift: true }],
      isEnabled: () => context.hasSelection && context.mode !== "textEdit",
      run: () => context.flipSelection("horizontal"),
    },
    {
      id: "object.flipVertical",
      label: "Flip Vertical",
      shortcuts: [{ key: "v", shift: true }],
      isEnabled: () => context.hasSelection && context.mode !== "textEdit",
      run: () => context.flipSelection("vertical"),
    },
    nudgeAction("edit.nudgeLeft", "Nudge Left", "arrowleft", -1, 0, context),
    nudgeAction("edit.nudgeRight", "Nudge Right", "arrowright", 1, 0, context),
    nudgeAction("edit.nudgeUp", "Nudge Up", "arrowup", 0, -1, context),
    nudgeAction("edit.nudgeDown", "Nudge Down", "arrowdown", 0, 1, context),
  ];
}

function booleanAction(
  id: string,
  label: string,
  key: string,
  operation: BooleanOperation,
  context: EditorActionContext,
): EditorAction {
  return {
    id,
    label,
    shortcuts: [{ key, mod: true, alt: true }],
    isEnabled: () => context.selectedCount >= 2 && context.mode !== "textEdit",
    run: () => context.booleanSelection(operation),
  };
}

function alignAction(
  id: string,
  label: string,
  key: string,
  action: AlignmentAction,
  context: EditorActionContext,
): EditorAction {
  return {
    id,
    label,
    shortcuts: [{ key, alt: true }],
    isEnabled: () => context.selectedCount >= 1 && context.mode !== "textEdit",
    run: () => context.alignSelection(action),
  };
}

function shapeToolAction(
  id: string,
  label: string,
  key: string,
  kind: PrimitiveKind,
  context: EditorActionContext,
): EditorAction {
  return {
    id,
    label,
    shortcuts: [{ key }],
    isEnabled: () => !context.devModeActive,
    run: () => {
      context.setShapeTool(kind);
      context.setMessage(`${label.replace(" Tool", "")} tool`);
    },
  };
}

function nudgeAction(
  id: string,
  label: string,
  key: string,
  dx: number,
  dy: number,
  context: EditorActionContext,
): EditorAction {
  return {
    id,
    label,
    shortcuts: [{ key, shift: "optional", alt: "optional" }],
    isEnabled: () => context.hasSelection && context.mode !== "textEdit",
    run: event => {
      const distance = event.shiftKey ? 10 : 1;
      context.nudgeSelection(dx * distance, dy * distance);
    },
  };
}
